import { useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { EASE, Reveal } from "@/components/Reveal";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Field, inputCls } from "@/components/forms";

export default function Login() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/request-link", { email });
      setSent(true);
    } catch {
      setSent(true); // same outward state regardless of outcome
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen">
      <SiteHeader />
      <main className="mx-auto max-w-xl px-6 pb-28 pt-36">
        {sent ? (
          <motion.div
            data-testid="login-sent"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="pt-16 text-center"
          >
            <img
              src="/assets/o-mark.jpeg"
              alt="ō"
              className="mx-auto h-12 w-12 rounded-full object-cover"
            />
            <div className="mx-auto mt-8 h-px w-10 bg-ochre" />
            <h1 className="mt-8 font-garamond text-4xl text-ink">
              Check your inbox.
            </h1>
            <p className="mx-auto mt-6 max-w-md font-garamond text-lg leading-relaxed text-softblack/85">
              If the address is eligible, a private sign-in link is on its
              way. It expires in 15 minutes and works once.
            </p>
          </motion.div>
        ) : (
          <>
            <Reveal>
              <p className="font-oswald text-[11px] uppercase tracking-[0.4em] text-seaglass">
                Member Access
              </p>
              <h1 className="mt-4 font-garamond text-4xl text-ink sm:text-5xl">
                Sign in
              </h1>
              <p className="mt-6 font-garamond text-lg leading-relaxed text-softblack/85">
                Enter the email associated with your membership and we will
                send you a private sign-in link.
              </p>
            </Reveal>
            <form
              data-testid="login-form"
              onSubmit={submit}
              className="mt-14 space-y-8"
            >
              <Field label="Email" htmlFor="login_email">
                <input
                  id="login_email"
                  type="email"
                  data-testid="login-email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputCls}
                />
              </Field>
              <div className="pt-2">
                <button
                  type="submit"
                  data-testid="login-submit"
                  disabled={submitting}
                  className="border border-ink px-10 py-4 font-oswald text-xs uppercase tracking-[0.35em] text-ink transition-colors duration-300 hover:bg-ink hover:text-paper disabled:opacity-50"
                >
                  {submitting ? "Sending…" : "Email me a sign-in link"}
                </button>
              </div>
            </form>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
